<template>
  <NotificationGroup
      group="bottom"
  >
    <div class="fixed inset-x-0 bottom-0 flex items-start justify-end p-6 px-4 py-6 pointer-events-none">
      <div class="w-full max-w-sm">
        <Notification
            v-slot="{ notifications }"
            enter="ease-out duration-300 transition"
            enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
            enter-to="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-500"
            leave-from="opacity-100"
            leave-to="opacity-0"
            move="transition duration-500"
            move-delay="delay-300"
        >
          <div
              v-for="notification in notifications"
              :key="notification.id"
              class="flex w-full max-w-sm mx-auto mt-4 overflow-hidden bg-white rounded-lg shadow-md"
          >
            <transition enter-active-class="transform ease-out duration-300 transition"
                        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
                        leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
              <div v-if="show"
                   class="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
                <div class="p-4">
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <CheckCircleIcon v-if="notification.type === 'success'" class="h-10 w-10 text-green-400"
                                       aria-hidden="true"/>
                      <ExclamationCircleIcon v-if="notification.type === 'error'" class="h-10 w-10 text-warning"
                                             aria-hidden="true"/>
                    </div>
                    <div class="ml-3 w-0 flex-1 pt-0.5">
                      <p class="text-base font-medium text-gray-900">{{ notification.title }}</p>
                      <p class="mt-1 text-sm text-gray-500">{{ notification.text }}</p>
                    </div>
                    <div class="ml-4 flex-shrink-0 flex">
                      <button type="button" @click="show = false"
                              class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <span class="sr-only">Close</span>
                        <XIcon class="h-5 w-5" aria-hidden="true"/>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </Notification>
      </div>
    </div>
  </NotificationGroup>
  <div class="md:flex md:items-center md:justify-between mb-3">
    <div class="flex-1 min-w-0">
      <h2 class="text-xl font-bold tracking-tight text-foreground lg:text-2xl">
        Facture
      </h2>
    </div>
    <div class="mt-4 flex flex-wrap gap-2 md:mt-0 md:ml-4">
      <!-- View Mode Toggle -->
      <div class="inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
        <button @click="viewMode = 'list'" :class="[viewMode === 'list' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50', 'px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center transition-all duration-150']">
          <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          Liste
        </button>
        <button @click="viewMode = 'timeline'" :class="[viewMode === 'timeline' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50', 'px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center transition-all duration-150']">
          <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Timeline
        </button>
        <button @click="viewMode = 'account'" :class="[viewMode === 'account' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50', 'px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center transition-all duration-150']">
          <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Compte
        </button>
      </div>

      <!-- Advanced Filters -->
      <Menu as="div" class="relative inline-block text-left">
        <MenuButton class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filtres
          <span v-if="activeFiltersCount > 0" class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {{ activeFiltersCount }}
          </span>
        </MenuButton>
        <MenuItems class="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-20" v-slot="{ close: closeMenu }">
          <div class="p-4 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Fournisseur
                </span>
              </label>
              <Combobox v-model="supplierFilter" nullable>
                <div class="relative">
                  <ComboboxInput
                      ref="supplierInput"
                      class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pl-10"
                      @change="supplierQuery = $event.target.value"
                      @focus="handleSupplierFocus"
                      @blur="handleSupplierBlur"
                      @click="handleSupplierClick"
                      :display-value="(supplier) => supplier"
                      placeholder="Rechercher un fournisseur..."
                  />
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <ComboboxOptions class="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    <ComboboxOption value="" v-slot="{ active, selected }">
                      <li :class="['cursor-pointer select-none py-2 pl-10 pr-4', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                        <div class="flex items-center">
                          <svg class="h-5 w-5 mr-3" :class="active ? 'text-white' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                          </svg>
                          <span :class="[selected ? 'font-semibold' : 'font-normal']">Tous les fournisseurs</span>
                        </div>
                        <span v-if="selected" :class="['absolute inset-y-0 left-0 flex items-center pl-3', active ? 'text-white' : 'text-indigo-600']">
                          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </span>
                      </li>
                    </ComboboxOption>
                    <ComboboxOption v-for="supplier in suppliersWithAvatars" :key="supplier.name" :value="supplier.name" v-slot="{ active, selected }">
                      <li :class="['cursor-pointer select-none py-2 pl-10 pr-4', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                        <div class="flex items-center">
                          <img v-if="supplier.avatar" :src="supplier.avatar" class="h-6 w-6 rounded-full mr-3" @error="onImageLoadFailure($event)" />
                          <svg v-else class="h-5 w-5 mr-3" :class="active ? 'text-white' : 'text-indigo-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span :class="[selected ? 'font-semibold' : 'font-normal']">{{ supplier.name }}</span>
                        </div>
                        <span v-if="selected" :class="['absolute inset-y-0 left-0 flex items-center pl-3', active ? 'text-white' : 'text-indigo-600']">
                          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </span>
                      </li>
                    </ComboboxOption>
                  </ComboboxOptions>
                </div>
              </Combobox>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Plage de dates
                </span>
              </label>
              <div class="grid grid-cols-2 gap-2">
                <input type="date" v-model="dateRangeStart" @focus="$event.target.showPicker()" class="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                <input type="date" v-model="dateRangeEnd" @focus="$event.target.showPicker()" class="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Montant (€)
                </span>
              </label>
              <div class="grid grid-cols-2 gap-2">
                <input type="number" v-model="amountMin" placeholder="Min" class="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                <input type="number" v-model="amountMax" placeholder="Max" class="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Compte comptable
                </span>
              </label>
              <select v-model="billingAccountFilter" class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                <option value="">Tous les comptes</option>
                <option v-for="account in uniqueBillingAccounts" :key="account.id" :value="account.id">
                  {{ account.id }} - {{ account.name }}
                </option>
              </select>
            </div>

            <div class="space-y-2">
              <label class="flex items-center">
                <input type="checkbox" v-model="filterOnlyCurrentFiscalYear" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                <span class="ml-2 text-sm text-gray-700">Exercice actuel uniquement</span>
              </label>
              <label class="flex items-center">
                <input type="checkbox" v-model="filterOutOfFiscalYear" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                <span class="ml-2 text-sm text-gray-700">Hors exercice uniquement</span>
              </label>
              <label class="flex items-center">
                <input type="checkbox" v-model="filterOldInvoices" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                <span class="ml-2 text-sm text-gray-700">Anciennes (>30 jours)</span>
              </label>
            </div>

            <div class="pt-2 border-t border-gray-200 flex gap-2">
              <button @click="clearFilters" class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Réinitialiser
              </button>
              <button @click="closeMenu" class="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700">
                Appliquer
              </button>
            </div>
          </div>
        </MenuItems>
      </Menu>

      <!-- Search -->
      <div class="relative rounded-md shadow-sm flex-1 md:flex-initial md:w-64">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
            v-model="searchQuery"
            type="text"
            name="query"
            placeholder="Rechercher..."
            class="focus:ring-indigo-500 px-4 py-2 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
            @input="saveToLocalStorage('filterKey', $event.target.value)"
        >
      </div>

      <!-- Action buttons group -->
      <a
          v-if="userData.fiscal_year"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
          :href="userData.fiscal_year.sheet_id != null ? 'https://docs.google.com/spreadsheets/d/' + userData.fiscal_year.sheet_id: ''"
      >
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Google Sheet
      </a>
      <button
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          type="button"
          @click="showUpload = true"
      >
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        Importer
      </button>
    </div>
  </div>
  <div class="sm:hidden">
    <label
        class="sr-only"
        for="tabs"
    >Select a tab</label>
    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
    <select
        id="tabs"
        class="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
        name="tabs"
    >
      <option
          v-for="tab in tabs"
          :key="tab.name"
          :selected="tab.current"
      >
        {{ tab.name }}
      </option>
    </select>
  </div>

  <!-- Statistics Banner -->
  <div class="mt-4 mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
    <div class="bg-gradient-to-br from-indigo-50 to-indigo-100 overflow-hidden rounded-xl border border-indigo-200">
      <div class="px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <dt class="text-xs font-medium text-indigo-600 uppercase tracking-wide">{{ statisticsTitle }}</dt>
            <dd class="mt-1 text-2xl font-bold text-indigo-900">{{ statistics.totalInvoices }}</dd>
          </div>
          <div class="flex-shrink-0">
            <svg class="h-10 w-10 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-gradient-to-br from-emerald-50 to-emerald-100 overflow-hidden rounded-xl border border-emerald-200">
      <div class="px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <dt class="text-xs font-medium text-emerald-600 uppercase tracking-wide">Montant total HT</dt>
            <dd class="mt-1 text-2xl font-bold text-emerald-900">{{ statistics.totalAmount }} €</dd>
          </div>
          <div class="flex-shrink-0">
            <svg class="h-10 w-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden rounded-xl border border-blue-200">
      <div class="px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <dt class="text-xs font-medium text-blue-600 uppercase tracking-wide">TVA</dt>
            <dd class="mt-1 text-2xl font-bold text-blue-900">{{ statistics.totalTax }} €</dd>
          </div>
          <div class="flex-shrink-0">
            <svg class="h-10 w-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-gradient-to-br from-yellow-50 to-yellow-100 overflow-hidden rounded-xl border border-yellow-200">
      <div class="px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <dt class="text-xs font-medium text-yellow-600 uppercase tracking-wide">Hors exercice</dt>
            <dd class="mt-1 text-2xl font-bold text-yellow-900">{{ statistics.outOfFiscalYear }}</dd>
          </div>
          <div class="flex-shrink-0">
            <svg class="h-10 w-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-gradient-to-br from-red-50 to-red-100 overflow-hidden rounded-xl border border-red-200">
      <div class="px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <dt class="text-xs font-medium text-red-600 uppercase tracking-wide">Anciennes >30j</dt>
            <dd class="mt-1 text-2xl font-bold text-red-900">{{ statistics.oldInvoices }}</dd>
          </div>
          <div class="flex-shrink-0">
            <svg class="h-10 w-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-0 flex flex-col">
    <div class="-my-2 -mx-4 overflow-x-hidden sm:-mx-6 lg:-mx-8 grid mt-1">
      <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
        <!-- Onglets de statut, fusionnés avec le tableau (haut de carte) -->
        <div class="hidden sm:block">
          <nav aria-label="Tabs"
               class="flex space-x-6 border border-gray-200 bg-gray-50 px-4"
               :class="viewMode === 'list' ? 'rounded-t-lg border-b-0' : 'mb-4 rounded-lg shadow-sm'">
            <router-link
                v-for="tab in tabs"
                :key="tab.name"
                :aria-current="tab.current ? 'page' : undefined"
                :class="[tab.href === $route.path ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'group inline-flex items-center py-2.5 px-1 border-b-2 font-medium text-sm']"
                :href="tab.href"
                :to="tab.href"
            >
              <component
                  :is="tab.icon"
                  :class="[tab.href === $route.path ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-1.5 h-4 w-4']"
                  aria-hidden="true"
              />
              <span>{{ tab.name }}</span>
            </router-link>
          </nav>
        </div>

        <!-- Timeline View -->
        <div v-if="viewMode === 'timeline'" class="space-y-6">
          <div v-for="(group, period) in timelineData" :key="period" class="bg-white shadow rounded-lg overflow-hidden">
            <div class="bg-gray-100 px-4 py-3 border-b border-gray-300">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900">{{ period }}</h3>
                <div class="flex items-center gap-4 text-sm text-gray-600">
                  <span>{{ group.length }} facture(s)</span>
                  <span class="font-semibold">{{ group.reduce((sum, inv) => sum + parseFloat(inv.amount?.replace(' €', '') || 0), 0).toFixed(2) }} €</span>
                </div>
              </div>
            </div>
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
              <InvoiceGrid
                  :data1="group"
                  :columns="gridColumns"
                  :columns-displayed="gridColumnsDisplayed"
                  :filter-key="searchQuery"
                  :supplier-filter="supplierFilter"
                  :page="1"
                  :per-page="100"
                  :pages="[]"
                  :fetchDone="true"
              />
            </div>
          </div>
        </div>

        <!-- Account View -->
        <div v-else-if="viewMode === 'account'" class="space-y-6">
          <div v-for="account in accountData" :key="account.accountId" class="bg-white shadow rounded-lg overflow-hidden">
            <div class="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-indigo-200">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex items-center justify-center min-w-[60px] h-12 px-3 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                    {{ account.accountId }}
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ account.accountName }}</h3>
                    <p class="text-xs text-gray-600">Compte {{ account.accountId }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-4 text-sm text-gray-600">
                  <span>{{ account.invoices.length }} facture(s)</span>
                  <span class="font-semibold text-lg text-indigo-900">{{ account.totalAmount.toFixed(2) }} €</span>
                </div>
              </div>
            </div>
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
              <InvoiceGrid
                  :data1="account.invoices"
                  :columns="gridColumns"
                  :columns-displayed="gridColumnsDisplayed"
                  :filter-key="searchQuery"
                  :supplier-filter="supplierFilter"
                  :page="1"
                  :per-page="100"
                  :pages="[]"
                  :fetchDone="true"
              />
            </div>
          </div>
        </div>

        <!-- List View : bordure identique à celle des onglets pour un contour continu -->
        <div v-else class="overflow-hidden border border-gray-200 shadow md:rounded-b-lg">
          <InvoiceGrid
              :data1="gridData"
              :columns="gridColumns"
              :columns-displayed="gridColumnsDisplayed"
              :filter-key="searchQuery"
              :supplier-filter="supplierFilter"
              :page="1"
              :per-page="25"
              :pages="[]"
              :fetchDone="fetchDone"
          />
        </div>
      </div>
    </div>
  </div>
  <TransitionRoot as="template" :show="showUpload" @close="showUpload = false">
    <InvoiceUpload></InvoiceUpload>
  </TransitionRoot>
</template>

<script setup>
import {TransitionRoot} from '@headlessui/vue'
import {CheckCircleIcon, ChevronRightIcon, MailIcon, ExclamationCircleIcon} from '@heroicons/vue/solid'
import {CreditCardIcon, OfficeBuildingIcon, SearchIcon, UserIcon, UsersIcon} from '@heroicons/vue/solid'

import {Menu, MenuButton, MenuItem, MenuItems, Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption} from '@headlessui/vue'
import {
  ArchiveIcon,
  ArrowCircleRightIcon,
  ChevronDownIcon,
  DuplicateIcon,
  HeartIcon,
  PencilAltIcon,
  TrashIcon,
  UserAddIcon,
  CheckIcon,
} from '@heroicons/vue/solid'
import {ref, computed} from 'vue'
import {notify, Notification, NotificationGroup} from 'notiwind'

const tabs = [
  {name: 'Toutes', href: '/invoice', icon: UserIcon, current: false},
  {name: 'En attente', href: '/invoice/backlog', icon: UsersIcon, current: true},
  {name: 'Associée', href: '/invoice/linked', icon: OfficeBuildingIcon, current: false},
  {name: 'Traitée', href: '/invoice/done', icon: CreditCardIcon, current: false},
  {name: 'Problème', href: '/invoice/issue', icon: CreditCardIcon, current: false},
  {name: 'Échéancier', href: '/invoice/timeline', icon: CreditCardIcon, current: false},
  {name: 'Supprimée', href: '/invoice/deleted', icon: CreditCardIcon, current: false},
]
</script>


<script>
import InvoiceGrid from './InvoiceGrid.vue'
import InvoiceUpload from './InvoiceUpload.vue'
import axios from "axios";
import moment from 'moment'
import {usePost} from "@/composables/fetch";
import {XIcon} from '@heroicons/vue/solid'
import router from "@/router";

export default {
  components: {
    InvoiceGrid,
    InvoiceUpload
  },
  props: {
    endpoint: String,
  },
  data() {
    return {
      show: 1,
      notifications: "",
      token: null,
      showUpload: false,
      supplierFilter: '',
      supplierQuery: '',
      showSupplierDropdown: false,
      gridColumnsDisplayed: {
        company_name: "Fournisseur",
        status_name: "Status",
        amount: "Montant",
        billing_account: "Compte",
        comment: "Commentaire",
        date: "Date",
        button: "",
      },
      gridColumns: ['id', 'invoice_id', 'company_name', 'date'],
      gridData: [],
      gridDataRaw: [], // Store raw data with full invoice objects
      fetchDone: false,
      searchQuery: '',
      polling: null,
      pdfExportInProgress: false,
      userData: {
        fiscal_year: {
          sheet_id: null
        }
      },
      // Advanced filters
      dateRangeStart: '',
      dateRangeEnd: '',
      amountMin: '',
      amountMax: '',
      billingAccountFilter: '',
      filterOnlyCurrentFiscalYear: false,
      filterOutOfFiscalYear: false,
      filterOldInvoices: false,
      viewMode: 'list' // 'list' or 'timeline'
    }
  },
  computed: {
    uniqueSuppliers() {
      const suppliers = this.gridData
        .map(item => item.company_name)
        .filter(name => name != null);
      return [...new Set(suppliers)].sort();
    },
    filteredSuppliers() {
      return this.supplierQuery === ''
        ? this.uniqueSuppliers
        : this.uniqueSuppliers.filter((supplier) => {
            return supplier.toLowerCase().includes(this.supplierQuery.toLowerCase())
          })
    },
    suppliersWithAvatars() {
      const suppliersMap = new Map();

      // Build map of suppliers with their avatars
      this.gridDataRaw.forEach(invoice => {
        if (invoice.company && invoice.company.name) {
          if (!suppliersMap.has(invoice.company.name)) {
            suppliersMap.set(invoice.company.name, {
              name: invoice.company.name,
              avatar: invoice.company.avatar
            });
          }
        }
      });

      // Convert to array and sort
      const suppliers = Array.from(suppliersMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      // Filter by query
      return this.supplierQuery === ''
        ? suppliers
        : suppliers.filter(s => s.name.toLowerCase().includes(this.supplierQuery.toLowerCase()));
    },
    uniqueBillingAccounts() {
      const accountsMap = new Map();
      this.gridDataRaw.forEach(invoice => {
        if (invoice.billing_account) {
          const id = invoice.billing_account.id;
          const name = invoice.billing_account.name;
          if (!accountsMap.has(id)) {
            accountsMap.set(id, { id, name });
          }
        }
      });
      return Array.from(accountsMap.values()).sort((a, b) => a.id - b.id);
    },
    statisticsTitle() {
      return {
        '/invoice': 'Factures',
        '/invoice/backlog': 'Factures en attente',
        '/invoice/linked': 'Factures associées',
        '/invoice/done': 'Factures traitées',
        '/invoice/issue': 'Factures en problème',
        '/invoice/timeline': 'Échéancier',
        '/invoice/deleted': 'Factures supprimées',
      }[this.endpoint] || 'Factures';
    },
    statistics() {
      const now = moment();
      const totalInvoices = this.gridDataRaw.length;
      const totalAmount = this.gridDataRaw.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0).toFixed(2);
      const totalTax = this.gridDataRaw.reduce((sum, inv) => {
        // amount_tax is the tax amount itself from API
        const tax = parseFloat(inv.amount_tax || 0);
        return sum + Math.abs(tax);
      }, 0).toFixed(2);
      const outOfFiscalYear = this.gridDataRaw.filter(inv => inv.isOutOfFiscalYear).length;
      const oldInvoices = this.gridDataRaw.filter(inv => {
        if (!inv.created_at) return false;
        return now.diff(moment(inv.created_at), 'days') > 30;
      }).length;

      return {
        totalInvoices,
        totalAmount,
        totalTax,
        outOfFiscalYear,
        oldInvoices
      };
    },
    activeFiltersCount() {
      let count = 0;
      if (this.supplierFilter) count++;
      if (this.dateRangeStart) count++;
      if (this.dateRangeEnd) count++;
      if (this.amountMin) count++;
      if (this.amountMax) count++;
      if (this.billingAccountFilter) count++;
      if (this.filterOnlyCurrentFiscalYear) count++;
      if (this.filterOutOfFiscalYear) count++;
      if (this.filterOldInvoices) count++;
      return count;
    },
    timelineData() {
      const grouped = {};

      this.gridData.forEach(invoice => {
        if (!invoice.date) return;

        // Group by month
        const date = moment(invoice.date);
        const monthKey = date.format('MMMM YYYY');

        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(invoice);
      });

      // Sort by date (most recent first)
      const sorted = {};
      Object.keys(grouped)
        .sort((a, b) => moment(b, 'MMMM YYYY').diff(moment(a, 'MMMM YYYY')))
        .forEach(key => {
          sorted[key] = grouped[key];
        });

      return sorted;
    },
    accountData() {
      const grouped = {};

      this.gridData.forEach(invoice => {
        if (!invoice.billing_account) return;

        const accountKey = invoice.billing_account;

        if (!grouped[accountKey]) {
          grouped[accountKey] = {
            accountId: invoice.billing_account,
            accountName: invoice.billing_account_name,
            invoices: [],
            totalAmount: 0
          };
        }

        grouped[accountKey].invoices.push(invoice);
        const amount = parseFloat(invoice.amount?.replace(' €', '') || 0);
        grouped[accountKey].totalAmount += amount;
      });

      // Convert to array and sort by account ID
      return Object.values(grouped).sort((a, b) => {
        const idA = parseInt(a.accountId);
        const idB = parseInt(b.accountId);
        return idA - idB;
      });
    }
  },
  watch: {
    '$route': {
      handler: function (val, oldVal) {
        clearInterval(this.polling);
        this.searchQuery = this.getFromLocalStorage('filterKey')
        this.supplierFilter = this.getFromLocalStorage('supplierFilter') || ''
        this.fetchInvoiceData()
        this.fetchUserData();
        this.intervalFetchData()
      },
      immediate: true,
      deep: true
    },
    dateRangeStart() { this.fetchInvoiceData(); },
    dateRangeEnd() { this.fetchInvoiceData(); },
    amountMin() { this.fetchInvoiceData(); },
    amountMax() { this.fetchInvoiceData(); },
    billingAccountFilter() { this.fetchInvoiceData(); },
    filterOnlyCurrentFiscalYear() { this.fetchInvoiceData(); },
    filterOutOfFiscalYear() { this.fetchInvoiceData(); },
    filterOldInvoices() { this.fetchInvoiceData(); },
  },
  beforeUnmount() {
    clearInterval(this.polling)
  },
  methods: {
    intervalFetchData: function () {
      this.polling = setInterval(() => {
        this.fetchInvoiceData();
        this.fetchUserData();
      }, 10000);
    },
    saveToLocalStorage(item, value) {
      localStorage.setItem(item, value);
    },
    getFromLocalStorage(item) {
      return localStorage.getItem(item);
    },
    handleSupplierFocus(event) {
      this.supplierQuery = '';
      this.showSupplierDropdown = true;
      // Force le dropdown à s'ouvrir en simulant un input
      const inputEvent = new Event('input', { bubbles: true });
      event.target.dispatchEvent(inputEvent);
    },
    handleSupplierClick(event) {
      if (event.target.value) {
        // Si déjà rempli, on vide pour forcer l'ouverture
        this.supplierQuery = '';
        event.target.value = '';
        const inputEvent = new Event('input', { bubbles: true });
        event.target.dispatchEvent(inputEvent);
      }
    },
    handleSupplierBlur() {
      setTimeout(() => {
        this.showSupplierDropdown = false;
      }, 200);
    },
    onImageLoadFailure(event) {
      event.target.style.display = 'none';
    },
    async fetchUserData() {
      const token = await this.$auth.getAccessTokenSilently()
      const HTTP = axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      HTTP.get("https://billing.clafoutis.eu/api/user")
          .then(response => {
            this.userData = response.data
          })
          .catch(e => {
            console.error(e)
          })
    },
    async sheetExport() {
      this.pdfExportInProgress = true
      const token = await this.$auth.getAccessTokenSilently();

      const data = {
        fiscal_year_id: 3,
        user_id: 37,
      }

      const response = await usePost("invoice/export", data, token)
      window.location.href = response.data['url'];
      this.pdfExportInProgress = false
    },

    async calculate() {
      const token = await this.$auth.getAccessTokenSilently();
      const HTTP = axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      let gridData = [];
      HTTP.get('invoice/calculate')
          .then(response => {

          }).catch(e => {
        console.log(e)
      })
    },


    async fetchInvoiceData() {
      const token = await this.$auth.getAccessTokenSilently();
      const HTTP = axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      let gridData = [];
      let gridDataRaw = [];
      HTTP.get(this.endpoint)
          .then(response => {
            const {invoices} = response.data
            invoices.forEach((value) => {
              // Vérifier si la date de la facture est en dehors de son exercice fiscal
              const invoiceDate = value['date'] ? moment(value['date']) : null;
              const invoiceFiscalYear = value['fiscal_year'];

              let isOutOfFiscalYear = false;

              // Si la facture a une date et un exercice fiscal, vérifier la cohérence
              if (invoiceDate && invoiceFiscalYear) {
                const fiscalStart = moment(invoiceFiscalYear.startDate);
                const fiscalEnd = moment(invoiceFiscalYear.endDate);
                isOutOfFiscalYear = !invoiceDate.isBetween(fiscalStart, fiscalEnd, null, '[]');
              }

              // Check if old invoice
              const now = moment();
              const isOld = value['created_at'] && now.diff(moment(value['created_at']), 'days') > 30;

              // Rapprochement bancaire : nombre de transactions liées et couverture du montant
              const transactionCount = value['transaction_count'] || 0;
              const linkedAmount = Math.round(Math.abs(parseFloat(value['transaction_amount_sum']) || 0) * 100);
              const invoiceAmount = Math.round(Math.abs(parseFloat(value['amount']) || 0) * 100);
              const isCovered = transactionCount > 0 && invoiceAmount > 0 && linkedAmount === invoiceAmount;

              // Store raw data
              gridDataRaw.push({
                ...value,
                isOutOfFiscalYear,
                isOld,
                amount: value['amount'] ? parseFloat(value['amount']) : 0
              });

              gridData.push({
                id: value['id'].substr(0, 10),
                company_name: value['company'] ? value['company']['name'] : null,
                company_avatar: value['company'] && value['company']['avatar'] != null ? value['company']['avatar'] : 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png',
                status_color: value['status'] ? value['status']['color'] : null,
                status_name: value['status'] ? value['status']['name'] : null,
                amount: value['amount'] ? value['amount'] + " €" : null,
                amount_tax: value['amount_tax'] ? value['amount_tax'] + " €" : null,
                billing_account: value['billing_account'] ? value['billing_account']['id'] : null,
                billing_account_name: value['billing_account'] ? value['billing_account']['name'] : null,
                comment: value['comment'] ? value['comment'] : null,
                date: value['date'] ? moment(value['date']).format('YYYY-MM-DD') : null,
                created_at: value['created_at'] ? moment(value['created_at']).format('YYYY-MM-DD') : null,
                invoice_id: value['invoice_id'] ? value['invoice_id'] : null,
                isOutOfFiscalYear: isOutOfFiscalYear,
                isOld: isOld,
                transactionCount,
                transactionIsCovered: isCovered,
                transactionAmount: transactionCount ? (linkedAmount / 100).toFixed(2) : null,
                button: value['id']
              })
            });

            // Trier par date, plus récente en haut
            gridData.sort((a, b) => {
              if (!a.date) return 1;
              if (!b.date) return -1;
              return b.date.localeCompare(a.date);
            });

            this.gridData = this.applyAdvancedFilters(gridData);
            this.gridDataRaw = gridDataRaw;
            this.fetchDone = true;
          }).catch(e => {
        console.log(e)
      })
    },
    applyAdvancedFilters(data) {
      let filtered = [...data];

      // Date range filter
      if (this.dateRangeStart) {
        filtered = filtered.filter(inv => inv.date && inv.date >= this.dateRangeStart);
      }
      if (this.dateRangeEnd) {
        filtered = filtered.filter(inv => inv.date && inv.date <= this.dateRangeEnd);
      }

      // Amount filter
      if (this.amountMin) {
        filtered = filtered.filter(inv => {
          const amount = parseFloat(inv.amount);
          return !isNaN(amount) && amount >= parseFloat(this.amountMin);
        });
      }
      if (this.amountMax) {
        filtered = filtered.filter(inv => {
          const amount = parseFloat(inv.amount);
          return !isNaN(amount) && amount <= parseFloat(this.amountMax);
        });
      }

      // Fiscal year filters
      if (this.filterOnlyCurrentFiscalYear) {
        filtered = filtered.filter(inv => !inv.isOutOfFiscalYear);
      }
      if (this.filterOutOfFiscalYear) {
        filtered = filtered.filter(inv => inv.isOutOfFiscalYear);
      }

      // Old invoices filter
      if (this.filterOldInvoices) {
        filtered = filtered.filter(inv => inv.isOld);
      }

      // Billing account filter
      if (this.billingAccountFilter) {
        filtered = filtered.filter(inv => inv.billing_account == this.billingAccountFilter);
      }

      return filtered;
    },
    clearFilters() {
      this.supplierFilter = '';
      this.dateRangeStart = '';
      this.dateRangeEnd = '';
      this.amountMin = '';
      this.amountMax = '';
      this.billingAccountFilter = '';
      this.filterOnlyCurrentFiscalYear = false;
      this.filterOutOfFiscalYear = false;
      this.filterOldInvoices = false;
      this.fetchInvoiceData();
    },
    truncate(v) {
      const newline = v.indexOf('\n')
      return newline > 0 ? v.slice(0, newline) : v
    },
    formatDate(v) {
      return v.replace(/T|Z/g, ' ')
    }
  }
}
</script>
